<script setup lang="ts">
import { ref, watch } from 'vue';
import CheckoutOrderSummary from '../ui/CheckoutOrderSummary.vue';
import CheckoutServicesSummary from '../ui/CheckoutServicesSummary.vue';
import CheckoutContactSummary from '../ui/CheckoutContactSummary.vue';
import CheckoutMobileSummaryCard from './CheckoutMobileSummaryCard.vue';
import type { ResponsiveImage } from '@/types/Product';
import type { RoomTypeImage } from '@/types/schemas/nbc';
import type { SeparateItem, AuxService } from '@/utils/checkout/pricingExtractor';
import type { Traveler } from '@/types/Traveler';

interface RoomSelection {
  roomTypeName: string;
  passengersLabel?: string;
  price?: number;
  boardName?: string;
  boardDescription?: string | null;
  separateItems?: readonly SeparateItem[];
  roomCode?: string;
  images?: RoomTypeImage[];
  description?: string | null;
}

interface ServiceItem {
  name: string;
  label?: string;
  price?: number;
  nameLabel?: string;
  included: boolean;
}

interface Props {
  // Collapsed compact card (mobile only)
  collapsible?: boolean;
  dateRange?: string;
  paxLabel?: string;

  // Order summary
  hotelImage?: ResponsiveImage;
  hotelImageUrl?: string;
  hotelName?: string;
  hotelLocation?: string;
  organizerName?: string;
  travelType?: string;
  notes?: string[];
  checkIn?: string;
  checkOut?: string;
  nights?: number;
  rooms?: RoomSelection[];
  auxServices?: readonly AuxService[];
  totalPrice?: number;
  hasPriceDeviation?: boolean;
  priceDeviationAmount?: number;
  originalTotalPrice?: number;
  hasDiscount?: boolean;
  discountAmount?: number;
  discountPercent?: number;
  discountReasons?: readonly string[];
  discountOriginalPrice?: number;

  // Services summary
  services?: ServiceItem[];
  servicesTotalPrice?: number;
  servicesNote?: string;
  showServicesEdit?: boolean;

  // Contact summary
  travelers?: Traveler[];

  // Step gating (services shown at step > 0, contact at step > 1)
  activeStep?: number;
}

const props = withDefaults(defineProps<Props>(), {
  collapsible: false,
  dateRange: undefined,
  paxLabel: undefined,
  hotelImage: undefined,
  hotelImageUrl: undefined,
  hotelName: undefined,
  hotelLocation: undefined,
  organizerName: undefined,
  travelType: undefined,
  notes: () => [],
  checkIn: undefined,
  checkOut: undefined,
  nights: undefined,
  rooms: () => [],
  auxServices: () => [],
  totalPrice: undefined,
  hasPriceDeviation: false,
  priceDeviationAmount: undefined,
  originalTotalPrice: undefined,
  hasDiscount: false,
  discountAmount: undefined,
  discountPercent: undefined,
  discountReasons: () => [],
  discountOriginalPrice: undefined,
  services: () => [],
  servicesTotalPrice: undefined,
  servicesNote: undefined,
  showServicesEdit: false,
  travelers: () => [],
  activeStep: 0,
});

const emit = defineEmits<{
  'edit-room': [];
  'edit-services': [];
  'edit-contact': [];
}>();

// Expand on tap; auto-collapse again whenever the checkout advances to a new step.
const isExpanded = ref(false);

watch(
  () => props.activeStep,
  () => {
    isExpanded.value = false;
  }
);

// The footer's "all details" link opens the collapsed mobile card before scrolling to it.
defineExpose({
  expand: () => {
    isExpanded.value = true;
  },
});
</script>

<template>
  <div>
    <!-- Collapsed compact card (mobile) -->
    <CheckoutMobileSummaryCard
      v-if="props.collapsible && !isExpanded"
      :hotel-image-url="props.hotelImageUrl"
      :hotel-name="props.hotelName"
      :date-range="props.dateRange"
      :pax-label="props.paxLabel"
      @expand="isExpanded = true"
    />

    <!-- Full summary stack (desktop always, mobile once expanded) -->
    <template v-else>
      <CheckoutOrderSummary
        :hotel-image="props.hotelImage"
        :hotel-name="props.hotelName"
        :hotel-location="props.hotelLocation"
        :hotel-image-url="props.hotelImageUrl"
        :organizer-name="props.organizerName"
        :travel-type="props.travelType"
        :notes="props.notes"
        :check-in="props.checkIn"
        :check-out="props.checkOut"
        :nights="props.nights"
        :rooms="props.rooms"
        :aux-services="props.auxServices"
        :total-price="props.totalPrice"
        :show-edit-button="true"
        :padded-image="props.collapsible"
        :has-price-deviation="props.hasPriceDeviation"
        :price-deviation-amount="props.priceDeviationAmount"
        :original-total-price="props.originalTotalPrice"
        :has-discount="props.hasDiscount"
        :discount-amount="props.discountAmount"
        :discount-percent="props.discountPercent"
        :discount-reasons="props.discountReasons"
        :discount-original-price="props.discountOriginalPrice"
        @edit="emit('edit-room')"
      />

      <!-- Services Summary -->
      <transition
        :enter-from-class="'translate-x-full'"
        :leave-to-class="'-translate-x-1/3'"
        enter-active-class="transition-transform duration-300 ease-in-out translate-x-full"
        leave-active-class="transition-transform duration-300 ease-in-out -translate-x-1/3 "
        mode="in-out"
      >
        <CheckoutServicesSummary
          v-if="props.activeStep > 0"
          :services="props.services"
          :total-price="props.servicesTotalPrice"
          :note="props.servicesNote"
          :show-edit-button="props.showServicesEdit"
          @edit="emit('edit-services')"
        />
      </transition>

      <!-- Contact Summary -->
      <transition
        :enter-from-class="'translate-x-full'"
        :leave-to-class="'-translate-x-1/3'"
        enter-active-class="transition-transform duration-300 ease-in-out translate-x-full"
        leave-active-class="transition-transform duration-300 ease-in-out -translate-x-1/3 "
        mode="in-out"
      >
        <CheckoutContactSummary
          v-if="props.activeStep > 1"
          :travelers="props.travelers"
          :show-edit-button="true"
          @edit="emit('edit-contact')"
        />
      </transition>
    </template>
  </div>
</template>
